/** Curate search box → results (both paths), kept out of curate.tsx so the
 * ring branch is unit-testable.
 *
 * - previewEnabled (backend ring, OpenAlex slice 1b): two-source search via
 *   the backend; the caller opens the preview dialog. Nothing is added here.
 * - otherwise: the original path — browser → /api/s2 proxy → auto-add up to
 *   `limit` results (unchanged).
 */
import { searchPapers } from "@/services/s2-client";
import { deduplicatePapers, s2PaperToPaperCreate } from "@/services/dedup";
import { searchConcept, type ConceptSearchResponse } from "@/services/concept-search";
import { simplifySearchQuery } from "@/lib/search-query";
import type { PaperCreate } from "@/types/paper";

export type CurateSearchOutcome =
  | { kind: "preview"; response: ConceptSearchResponse }
  | { kind: "added"; count: number }
  | { kind: "empty"; title: string; description: string };

export async function runCurateSearch(opts: {
  conceptId: string;
  query: string;
  limit: number;
  previewEnabled: boolean;
  addPapers: (papers: PaperCreate[]) => void;
}): Promise<CurateSearchOutcome> {
  const { conceptId, query, limit, previewEnabled, addPapers } = opts;

  if (previewEnabled) {
    const response = await searchConcept(conceptId, query, limit);
    if (response.results.length === 0) {
      return {
        kind: "empty",
        title: "No results",
        description: `Nothing matched “${query}” on ${
          response.openalex_status === "off" ? "Semantic Scholar" : "Semantic Scholar or OpenAlex"
        }. Try fewer words or an identifier such as a catalogue number.`,
      };
    }
    return { kind: "preview", response };
  }

  let res = await searchPapers(query, { limit });
  // S2 relevance search returns total=0 for natural phrasing ("cosmology
  // of NGC 6164/6165"); retry once with stopwords/punctuation stripped
  // before telling the user there is nothing.
  const simplified = simplifySearchQuery(query);
  if ((!res || res.data.length === 0) && simplified && simplified !== query) {
    res = await searchPapers(simplified, { limit });
  }
  if (!res || res.data.length === 0) {
    return {
      kind: "empty",
      title: "No Semantic Scholar results",
      description: `Nothing matched “${query}”. Try fewer words or an identifier such as a catalogue number.`,
    };
  }
  const converted = res.data
    .map(s2PaperToPaperCreate)
    .filter((p): p is NonNullable<typeof p> => p !== null);
  const unique = deduplicatePapers(converted);
  if (unique.length > 0) addPapers(unique);
  return { kind: "added", count: unique.length };
}
